import React, {useCallback, useEffect, useRef, useState} from "react";
import Styles from "./styles.module.scss";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";

import copyIcon from '../../../theme/assets/svg/tc_cab_copy_icon.svg';
import radarIcon from '../../../theme/assets/svg/tc_cab_apostol_radar.svg';
import {formatWithSpaces} from "../../../helpers/formatSpace";
import {getReferrals, getUserData, setReferrer} from "../../../store/cabinet/action";

const CabinetApostolReferral = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const dispatch = useDispatch();

    const referrals = useSelector(store => store.cabinet.referrals);
    const userData = useSelector(store => store.cabinet.userData);
    const setReferrerStarted = useSelector(store => store.cabinet.setReferrerStarted);
    const setReferrerErr = useSelector(store => store.cabinet.setReferrerErr);

    const [copied, setCopied] = useState('');
    const [inviterCode, setInviterCode] = useState('');
    const copyTimer = useRef(null);

    useEffect(() => {
        return () => {
            if (copyTimer.current) {
                clearTimeout(copyTimer.current);
            }
        };
    }, []);

    const referralCode = referrals?.referral_code || userData?.user?.referral_code || '';
    const referralLink = referralCode ? `${window.location.origin}/sign-up/${referralCode}` : '';

    const levels = Array.isArray(referrals?.levels) ? referrals.levels : [];

    // Указать пригласителя можно один раз: пока его нет и пока сам никого не
    // привёл. Решает сервер — can_set_referrer, а has_referrer из GET /user
    // отличает «уже указан» от «уже поздно»
    const canSetReferrer = !!referrals?.can_set_referrer;
    const hasReferrer = !!userData?.user?.has_referrer;

    const inviterHandler = () => {
        const code = inviterCode.trim();
        if (!code || setReferrerStarted) {
            return;
        }

        dispatch(setReferrer(code, () => {
            setInviterCode('');
            dispatch(getReferrals());
            dispatch(getUserData());
        }));
    };

    // 422 errors.referral_code — нет такого кода, свой собственный,
    // пригласитель уже указан или у пользователя уже есть рефералы
    const inviterErr = setReferrerErr?.errors?.referral_code?.[0]
        || setReferrerErr?.message
        || '';

    const copyHandler = useCallback((key, value) => {
        if (!value || !navigator?.clipboard?.writeText) return;

        navigator.clipboard.writeText(value)
            .then(() => {
                setCopied(key);

                if (copyTimer.current) {
                    clearTimeout(copyTimer.current);
                }
                copyTimer.current = setTimeout(() => setCopied(''), 2000);
            })
            .catch(() => {});
    }, []);

    const fields = [
        { key: 'link', name: t('Реферальная ссылка'), value: referralLink },
        { key: 'code', name: t('Реферальный код'), value: referralCode }
    ];

    const stats = [
        { name: t('Всего рефералов'), value: referrals?.referrals?.total ?? 0 },
        { name: t('Активных рефералов'), value: referrals?.referrals?.active ?? 0 },
        { name: `${t('Заработано')} $TC`, value: referrals?.earned_total ?? 0 }
    ];

    return (
        <section className={`${Styles.container}`}>
            <div className={`${Styles.contentWrap} ${Styles.cabinetPC}`}>
                <div className={`${Styles.cardWrap} ${Styles.border3dOuterXl}`}>
                    <div className={`${Styles.cardTitle}`}>{t('Реферальная программа')}</div>

                    <div className={`${Styles.fieldsWrap}`}>
                        {fields.map((field) => {
                            return (
                                <div className={`${Styles.fieldWrap}`} key={'apostol-field_' + field.key}>
                                    <div className={`${Styles.fieldName}`}>
                                        {field.name}
                                        <span className={`${copied === field.key ? Styles.fieldCopiedActive : null}`}>{t('Скопировано')}</span>
                                    </div>
                                    <div className={`${Styles.fieldBox}`}>
                                        <div className={`${Styles.fieldValue}`}>{field.value || '...'}</div>
                                        <div
                                            className={`${Styles.fieldBtn} ${!field.value ? Styles.fieldBtnDisabled : null}`}
                                            onClick={() => copyHandler(field.key, field.value)}
                                            title={t('Скопировать')}
                                        >
                                            <img src={copyIcon} alt={t('Скопировать')}/>
                                        </div>
                                    </div>
                                </div>
                            )
                        })}

                        {canSetReferrer ? (
                            <div className={`${Styles.fieldWrap}`}>
                                <div className={`${Styles.fieldName}`}>{t('Код пригласителя')}</div>
                                <div className={`${Styles.fieldBox}`}>
                                    <input
                                        className={`${Styles.inviterInput}`}
                                        type="text"
                                        value={inviterCode}
                                        placeholder={t('Введите код')}
                                        maxLength={32}
                                        disabled={setReferrerStarted}
                                        onChange={(e) => setInviterCode(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') { inviterHandler(); } }}
                                    />
                                    <div
                                        className={`${Styles.fieldBtn} ${(!inviterCode.trim() || setReferrerStarted) ? Styles.fieldBtnDisabled : null}`}
                                        onClick={inviterHandler}
                                        title={t('Сохранить')}
                                    >
                                        <span className={`${Styles.inviterMark}`}>{setReferrerStarted ? '…' : '✓'}</span>
                                    </div>
                                </div>
                                {inviterErr ? <div className={`${Styles.inviterErr}`}>{inviterErr}</div> : null}
                            </div>
                        ) : null}

                        {!canSetReferrer && hasReferrer ? (
                            <div className={`${Styles.fieldWrap}`}>
                                <div className={`${Styles.fieldName}`}>{t('Код пригласителя')}</div>
                                <div className={`${Styles.inviterDone}`}>{t('Пригласитель указан')}</div>
                            </div>
                        ) : null}
                    </div>

                    <div className={`${Styles.statWrap}`}>
                        {stats.map((item, index) => {
                            return (
                                <div className={`${Styles.statItem}`} key={'apostol-stat_' + index}>
                                    <div className={`${Styles.statItemName}`}>{item.name}</div>
                                    <div className={`${Styles.statItemValue}`}>{formatWithSpaces(item.value)}</div>
                                </div>
                            )
                        })}
                    </div>

                    <div className={`${Styles.networkWrap}`}>
                        <div className={`${Styles.networkTitle}`}>{t('Структура сети')}</div>
                        <div className={`${Styles.networkBox}`}>
                            <div className={`${Styles.networkIconWrap}`}>
                                <img src={radarIcon} alt=""/>
                            </div>
                            <div className={`${Styles.networkList}`}>
                                {levels.length > 0 ? levels.map((item, index) => {
                                    return (
                                        <div className={`${Styles.networkListItem}`} key={'apostol-level_' + (item?.level ?? index)}>
                                            <div className={`${Styles.networkListItemName}`}>{item?.level} {t('уровень')}</div>
                                            <div className={`${Styles.networkListItemValue}`}>{formatWithSpaces(item?.referrals ?? 0)}</div>
                                        </div>
                                    )
                                }) : (
                                    <div className={`${Styles.networkEmpty}`}>{t('Данных пока нет')}</div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className={`${Styles.cardBtn}`} onClick={()=>{navigate('/apostol')}}>
                        {t('Подробнее о реферальной программе')}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default CabinetApostolReferral;
